/**
 * 选择人员弹窗（公用）
 * 左侧：人员列表（搜索 + 分页 + 勾选），按姓名或工号搜索
 * 右侧：已选择列表（可移除）
 * 数据来源：trpc.people.list
 */
import * as React from 'react';
import type { Selection } from 'react-aria-components';
import {
  Avatar,
  Button,
  Description,
  Label,
  ListBox,
  Modal,
  Pagination,
  SearchField,
  Surface,
  toast,
} from '@heroui/react';
import { IconX as X } from '@tabler/icons-react';

import { trpc } from '@/lib/trpc';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

export type PeopleSelectItem = {
  peopleId: string;
  fullName?: string | null;
  jobNumber?: string | null;
  avatarUrl?: string | null;
};

/** 用户关连筛选：已关连 = 仅展示 userId 不为空的人员，未关连 = 仅展示 userId 为空的人员 */
export type UserLinkFilter = 'linked' | 'unlinked';

export interface PeopleSelectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (selected: PeopleSelectItem[]) => void;
  title?: string;
  /** 已选中的人员 id 列表（用于回显排除） */
  excludePeopleIds?: string[];
  /** 最多可选人数，不传则无限制；超出时 toast 提示 */
  headcount?: number;
  /** 用户关连筛选：已关连 / 未关连；不传则不按用户关连筛选 */
  userLinkFilter?: UserLinkFilter;
}

const PAGE_SIZE = 10;

function selectionToIdSet(keys: Selection, visibleIds: string[]): Set<string> {
  if (keys === 'all') return new Set(visibleIds);
  return new Set(Array.from(keys, (k) => String(k)));
}

export function PeopleSelectDialog({
  open,
  onOpenChange,
  onConfirm,
  title = '选择人员',
  excludePeopleIds = [],
  headcount,
  userLinkFilter,
}: PeopleSelectDialogProps) {
  const [keyword, setKeyword] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [selected, setSelected] = React.useState<PeopleSelectItem[]>([]);

  const listFilter = React.useMemo(() => {
    if (userLinkFilter === 'unlinked') return { userId: null as null };
    if (userLinkFilter === 'linked') return { userIdNotNull: true };
    return undefined;
  }, [userLinkFilter]);

  const { data, isLoading } = trpc.people.list.useQuery(
    {
      page,
      pageSize: PAGE_SIZE,
      keyword: keyword.trim() || undefined,
      sortBy: 'fullName',
      sortOrder: 'asc',
      ...listFilter,
    },
    { enabled: open }
  );

  const items = (data?.items ?? []) as Array<{
    id: string;
    fullName?: string | null;
    jobNumber?: string | null;
    avatarUrl?: string | null;
  }>;

  const totalPages = data?.totalPages ?? 0;
  const totalItems = data?.total ?? 0;

  const visibleItems = React.useMemo(
    () => items.filter((item) => !excludePeopleIds.includes(item.id)),
    [items, excludePeopleIds]
  );

  const selectedKeys = React.useMemo(
    () => new Set(selected.map((s) => s.peopleId)),
    [selected]
  );

  const handlePeopleSelectionChange = React.useCallback(
    (keys: Selection) => {
      const visibleIds = visibleItems.map((i) => i.id);
      const visibleIdSet = new Set(visibleIds);
      const nextKeys = selectionToIdSet(keys, visibleIds);

      setSelected((prev) => {
        const kept = prev.filter((p) => !visibleIdSet.has(p.peopleId));
        const newlySelected: PeopleSelectItem[] = [];
        for (const id of nextKeys) {
          if (!visibleIdSet.has(id)) continue;
          const item = visibleItems.find((i) => i.id === id);
          if (!item) continue;
          newlySelected.push({
            peopleId: id,
            fullName: item.fullName ?? null,
            jobNumber: item.jobNumber ?? null,
            avatarUrl: item.avatarUrl ?? null,
          });
        }
        const merged = [...kept, ...newlySelected];
        if (headcount != null && merged.length > headcount) {
          toast.danger(`限制选择${headcount}人，选择已满。`);
          return prev;
        }
        return merged;
      });
    },
    [visibleItems, headcount]
  );

  const removeSelected = React.useCallback((peopleId: string) => {
    setSelected((prev) => prev.filter((p) => p.peopleId !== peopleId));
  }, []);

  const resetLocalState = React.useCallback(() => {
    setSelected([]);
    setKeyword('');
    setPage(1);
  }, []);

  const handleConfirm = React.useCallback(() => {
    onConfirm(selected);
    onOpenChange(false);
    resetLocalState();
  }, [onConfirm, onOpenChange, selected, resetLocalState]);

  const handleModalOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) resetLocalState();
      onOpenChange(next);
    },
    [onOpenChange, resetLocalState]
  );

  const displayFullName = (item: (typeof items)[number]) =>
    item.fullName ?? `人员 ${item.id ?? ''}`;
  const avatarLetter = (item: (typeof items)[number]) =>
    (item.fullName ?? '?').charAt(0).toUpperCase();

  const startItem = totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(page * PAGE_SIZE, totalItems);

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="lg">
          <Modal.Dialog className="flex h-[500px] max-h-[85vh] max-w-2xl flex-col">
            <Modal.Header>
              <Modal.Heading className="text-xl font-medium">{title}</Modal.Heading>
            </Modal.Header>
            <Modal.Body className="flex min-h-0 flex-1 flex-row gap-0 bg-surface-secondary rounded-3xl">
              {/* 左列：人员列表 */}
              <div className="flex h-full min-h-0 w-3/5 min-w-0 shrink-0 flex-col overflow-hidden border-r border-[var(--border)]">
                <div className="flex h-12 shrink-0 items-center border-b border-[var(--border)] px-4">
                  <h4 className="text-sm font-medium">人员列表</h4>
                </div>
                <div className="flex h-14 shrink-0 items-center px-4">
                  <SearchField
                    aria-label="搜索人员"
                    value={keyword}
                    onChange={(v) => {
                      setKeyword(v);
                      setPage(1);
                    }}
                  >
                    <SearchField.Group>
                      <SearchField.SearchIcon />
                      <SearchField.Input placeholder="按姓名或工号搜索" />
                      <SearchField.ClearButton />
                    </SearchField.Group>
                  </SearchField>
                </div>
                <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden px-4 py-2">
                  <Surface className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-3xl shadow-surface">
                    <div className="min-h-[120px] flex-1 overflow-y-auto p-1">
                      {isLoading ? (
                        <p className="text-muted px-2 py-2 text-sm">加载中…</p>
                      ) : visibleItems.length === 0 ? (
                        <p className="text-muted px-2 py-2 text-sm">暂无人员</p>
                      ) : (
                        <ListBox
                          aria-label="人员列表"
                          selectionMode="multiple"
                          selectedKeys={selectedKeys}
                          onSelectionChange={handlePeopleSelectionChange}
                        >
                          {visibleItems.map((item) => (
                            <ListBox.Item
                              key={item.id}
                              id={item.id}
                              textValue={`${displayFullName(item)} ${item.jobNumber ?? ''}`}
                            >
                              <Avatar size="sm">
                                {item.avatarUrl ? (
                                  <Avatar.Image src={item.avatarUrl} alt={displayFullName(item)} />
                                ) : null}
                                <Avatar.Fallback>{avatarLetter(item)}</Avatar.Fallback>
                              </Avatar>
                              <div className="flex min-w-0 flex-1 flex-col">
                                <Label className="truncate">{displayFullName(item)}</Label>
                                <Description className="text-muted text-xs">
                                  工号：{item.jobNumber ?? '—'}
                                </Description>
                              </div>
                              <ListBox.ItemIndicator />
                            </ListBox.Item>
                          ))}
                        </ListBox>
                      )}
                    </div>
                  </Surface>

                  <Pagination className="w-full shrink-0">
                    <Pagination.Summary>
                      {startItem} 至 {endItem} 共 {totalItems} 条
                    </Pagination.Summary>
                    <Pagination.Content>
                      <Pagination.Item>
                        <Pagination.Previous
                          isDisabled={page <= 1 || totalPages <= 0}
                          onPress={() => setPage((p) => Math.max(1, p - 1))}
                        >
                          <Pagination.PreviousIcon />
                          <span>上一页</span>
                        </Pagination.Previous>
                      </Pagination.Item>
                      <Pagination.Item>
                        <Pagination.Next
                          isDisabled={page >= totalPages || totalPages <= 0}
                          onPress={() => setPage((p) => Math.min(totalPages, p + 1))}
                        >
                          <span>下一页</span>
                          <Pagination.NextIcon />
                        </Pagination.Next>
                      </Pagination.Item>
                    </Pagination.Content>
                  </Pagination>
                </div>
              </div>

              {/* 右列：已选择 */}
              <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                <div className="flex h-12 shrink-0 items-center border-b border-[var(--border)] px-4">
                  <h4 className="text-sm font-medium">已选择</h4>
                </div>
                <div className="flex h-14 shrink-0 items-center px-4">
                  <p className="text-muted text-sm">{selected.length}人</p>
                </div>
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4">
                  <Surface className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-3xl shadow-surface">
                    <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
                      {selected.map((p) => (
                        <div
                          key={p.peopleId}
                          className="flex items-center gap-2 rounded-lg px-2 py-1.5"
                          style={{
                            background:
                              'color-mix(in oklch, var(--surface-secondary) 70%, transparent)',
                          }}
                        >
                          <Avatar className="size-6" size="sm">
                            {p.avatarUrl ? (
                              <Avatar.Image src={p.avatarUrl} alt={p.fullName ?? ''} />
                            ) : null}
                            <Avatar.Fallback className="text-xs">
                              {(p.fullName ?? '?').charAt(0).toUpperCase()}
                            </Avatar.Fallback>
                          </Avatar>
                          <div className="flex min-w-0 flex-1 flex-col">
                            <Label className="truncate text-sm">{p.fullName ?? p.peopleId}</Label>
                            <Description className="text-muted text-xs">
                              工号：{p.jobNumber ?? '—'}
                            </Description>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeSelected(p.peopleId)}
                            className="rounded p-0.5 hover:bg-[var(--surface-secondary)]"
                            aria-label="移除"
                          >
                            <X className="size-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </Surface>
                </div>
              </div>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="ghost" onPress={() => handleModalOpenChange(false)}>
                取消
              </Button>
              <Button
                isDisabled={selected.length === 0}
                onPress={handleConfirm}
              >
                确定
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
